import theme from "@shared/utils/themeColors";
import React, { useEffect } from "react";
import { useSupport } from "../context/SupportContext";
import { useSupportMessages } from "../hooks/useSupportMessages";
import SupportMessageInput from "./SupportMessageInput";
import SupportMessageList from "./SupportMessageList";

/**
 * Support chat window component
 * Used by the floating bubble for regular users
 */
const SupportChatWindow: React.FC = () => {
  const { closeChat, selectedTicketId, setSelectedTicketId } = useSupport();

  const {
    messages,
    loading,
    hasMore,
    loadMoreMessages,
    updateTypingStatus,
    sendMessage,
    deleteMessage,
    typingUsers,
  } = useSupportMessages(selectedTicketId);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      updateTypingStatus(false);
    };
  }, [updateTypingStatus]);

  return (
    <div className="flex flex-col w-[350px] h-[500px] bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-300 dark:border-gray-700 overflow-visible">
      {/* Chat header */}
      <div className={`flex items-center p-4 border-b ${theme.border.primary}`}>
        <div className="flex items-center">
          <div className="flex-shrink-0 mr-3">
            <img
              src="/assets/images/avatar.png"
              alt="GCCA Support"
              className="w-9 h-9 rounded-full"
            />
          </div>
          <div>
            <h3 className={`font-semibold ${theme.text.primary}`}>
              GCCA Support
            </h3>
            <p className={`text-xs ${theme.text.muted}`}>
              {typingUsers &&
              Object.values(typingUsers).some((isTyping) => isTyping)
                ? "Typing..."
                : "How can we help you today?"}
            </p>
          </div>
        </div>

        <button
          onClick={closeChat}
          className={`ml-auto ${theme.text.mutedHover}`}
          aria-label="Close chat"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Chat messages */}
      <div className="flex-grow flex flex-col overflow-y-auto overflow-x-visible">
        <SupportMessageList
          messages={messages}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={loadMoreMessages}
          onDeleteMessage={deleteMessage}
          typingUsers={typingUsers}
        />
      </div>

      {/* Chat input */}
      <SupportMessageInput
        onSendMessage={sendMessage}
        updateTypingStatus={updateTypingStatus}
      />
    </div>
  );
};

export default SupportChatWindow;
